import { useMatchesContext } from "@/contexts/TournamentContext";
import { detectWinner } from "@/lib/sports";
import { MatchStatus, PadelMatchResult, TeamStatistics } from "@/types";
import { useMemo } from "react";

export function useTeamStatistics(teamId: string | null | undefined) {
  const { matches, loading } = useMatchesContext();

  const teamStatistics: TeamStatistics | null = useMemo(() => {
    if (!teamId || !matches) return null;

    // Only count completed matches
    const teamMatches = matches.filter(
      (match) =>
        (match.team1Id === teamId || match.team2Id === teamId) &&
        match.status === MatchStatus.COMPLETED,
    );

    let wins = 0;
    let losses = 0;
    let gamesWon = 0;
    let gamesLost = 0;
    let setsWon = 0;
    let setsLost = 0;
    let tiebreaksWon = 0;
    let tiebreaksLost = 0;

    teamMatches.forEach((match) => {
      // Determine if this team won
      const winnerId = match.result?.walkover
        ? match.result.winnerId
        : detectWinner(match, match.result as any);
      if (winnerId === teamId) {
        wins += 1;
      } else if (winnerId) {
        losses += 1;
      }

      // Skip detailed stats for walkover matches (no actual games played)
      if (match.result?.walkover) return;

      // Calculate stats from match result
      if (match.result && match.sport === "padel") {
        const result = match.result as PadelMatchResult;
        const isTeam1 = match.team1Id === teamId;

        // Process each set
        const sets = [result.set1, result.set2, result.set3].filter(
          (set) => set.team1Games > 0 || set.team2Games > 0,
        );

        sets.forEach((set) => {
          const teamGames = isTeam1 ? set.team1Games : set.team2Games;
          const opponentGames = isTeam1 ? set.team2Games : set.team1Games;

          gamesWon += teamGames;
          gamesLost += opponentGames;

          // Count sets won/lost
          if (teamGames > opponentGames) {
            setsWon += 1;
          } else if (opponentGames > teamGames) {
            setsLost += 1;
          }

          // Count tiebreaks won/lost
          if (set.tieBreak) {
            const teamTiebreakPoints = isTeam1
              ? set.tieBreak.team1Points
              : set.tieBreak.team2Points;
            const opponentTiebreakPoints = isTeam1
              ? set.tieBreak.team2Points
              : set.tieBreak.team1Points;

            if (teamTiebreakPoints > opponentTiebreakPoints) {
              tiebreaksWon += 1;
            } else if (opponentTiebreakPoints > teamTiebreakPoints) {
              tiebreaksLost += 1;
            }
          }
        });
      }
    });

    const totalMatches = wins + losses;
    const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;

    return {
      teamId: teamId,
      wins,
      losses,
      winRate: Math.round(winRate),
      challengesSent: 0, // Placeholder, implement as needed
      challengesReceived: 0, // Placeholder, implement as needed
      gamesWon,
      gamesLost,
      setsWon,
      setsLost,
      tiebreaksWon,
      tiebreaksLost,
      opponentsBeaten: [], // Placeholder, implement as needed
      rankingHistory: [], // Placeholder, implement as needed
    };
  }, [teamId, matches]);

  return { teamStatistics, loading };
}
